import { ButtonColor, ButtonSize, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ClientInfoChange,
  ContactInfoIndividualPageValidation,
  Customer,
} from 'src/front/api/apiInstance';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import AsyncButton from 'src/front/components/AsyncButton';
import CustomerSearchForm, {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components/CustomerSearchForm';

import {
  Container,
  FormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components/Markdown';

import {
  useClearDispatch,
  useCustomerSearchDispatch,
  useCustomerSearchState,
  useSetClientNameDispatch,
} from 'src/front/contexts/ContactInfoContext';

const tableHeaders = [
  'changeFundClientInfo_customerTable_name_title',
  'changeFundClientInfo_middlename_label',
  'changeFundClientInfo_customerTable_birth_title',
  'changeFundClientInfo_gender_label',
  'changeFundClientInfo_customerTable_document_title',
];

interface Props {
  goToNextStep: (customerId?: string) => void;
  clearCustomerSearch: () => void;
}

export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}

const ContactInfoSearch: FC<Props> = ({ goToNextStep, clearCustomerSearch }) => {
  const customerSearchDispatch = useCustomerSearchDispatch();
  const {
    customerSearchValues,
    pageNumber: initialCustomerPage,
    customerId: selectedCustomerId,
  } = useCustomerSearchState();
  const clearCustomerSearchState = useClearDispatch();
  const clientNameDispatch = useSetClientNameDispatch();
  const { t } = useTranslation();

  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [customerPage, setCustomerPage] = useState<number>(initialCustomerPage || 1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const [warning, setWarning] = useState<string | undefined>('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow>();
  const [selectedCustomerPage, setSelectedCustomerPage] = useState<number>();

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );

  const loadCustomerList = useCallback(
    async (
      pageNumber?: number,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<CustomerTableRow[]> => {
      try {
        setLoadingTable(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || customerPage,
          refresh: !pageNumber || forceRefresh,
        });
        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setCustomerList(customers);
        setHasNextPage(data.hasNextPage);
        setCustomerPage(pageNumber || 1);
        setLoadingTable(false);
        setWarning(data.warning);

        return customers;
      } catch (error) {
        // TODO (abdulbasid): show error on modal or handle with error boundary

        return [];
      }
    },
    [customerPage, searchFormValues]
  );

  const loadPageItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      loadCustomerList(pageNumber);
    },
    [loadCustomerList]
  );

  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      const customers = await loadCustomerList(initialCustomerPage, true);
      setSelectedCustomer(customers?.find((customer) => customer.id === selectedCustomerId));
    };

    if (!customerList && !selectedCustomer && selectedCustomerId) {
      loadPreviousValues();
    }
  }, [
    customerList,
    customerSearchValues,
    initialCustomerPage,
    loadCustomerList,
    selectedCustomer,
    selectedCustomerId,
  ]);

  const onSearchSubmit = (formData: SearchFormData): void => {
    setSearchFormValues(formData);
    clearCustomerSearchState();
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCustomerPage && setSelectedCustomerPage(undefined);
    loadCustomerList(1, true, formData);
    clearCustomerSearch();
  };

  const handleSelectCustomer = (customer: CustomerTableRow | undefined): void => {
    setSelectedCustomer(customer);
    setSelectedCustomerPage(customerPage);
    warning && setWarning(undefined);
  };

  const onCustomerSubmit = async (): Promise<void> => {
    const { customerName, customerSecondName, customerSurname, birthDate } = searchFormValues;
    const { data } = await ContactInfoIndividualPageValidation.checkContactInfo({
      customerId: selectedCustomer?.id || '',
      customerName,
      customerSecondName,
      customerSurname,
      birthDate: formatDate(birthDate),
    });

    if (data.result === 'OK') {
      goToNextStep(selectedCustomer?.id);

      customerSearchDispatch({
        customerId: selectedCustomer?.id,
        customerNumber: selectedCustomer?.customerNumber,
        customerSearchValues: {
          ...searchFormValues,
          selectedCustomer,
        },
        pageNumber: selectedCustomerPage,
      });
    }
    const { data: clientNameResponse } = await ClientInfoChange.getClientName({
      customerName,
      customerSecondName: selectedCustomer?.columns[1],
      customerSurname,
    });
    if (data.result && clientNameResponse.result === 'OK') {
      clientNameDispatch({ clientName: clientNameResponse });
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (!selectedCustomer) {
      setWarning(t('changeFundClientInfo_noSelectedCustomer_errorMessage'));
    } else {
      await onCustomerSubmit();
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="changeFundClientInfo_title" />
      </Title>
      <Separator />
      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_customerInfo_text" />
      </Subtitle>
      <CustomerSearchForm formValues={searchFormValues} onSubmit={onSearchSubmit} />
      {customerList ? (
        <>
          <Subtitle>
            <Trans i18nKey="changeFundClientInfo_clientList_text" />
          </Subtitle>
          <TableWrapper>
            <Table
              emptyTableText={t('general_components_notFound_errorMessage')}
              hasNextPage={hasNextPage}
              headers={tableHeaders.map((head) => t(head))}
              items={customerList}
              loading={loadingTable}
              loadPageItems={loadPageItems}
              onSelect={handleSelectCustomer}
              pageNumber={customerPage}
              selectedItem={selectedCustomer}
              widths={['20', '20', '28', '12', '20']}
              withRadio
            />
          </TableWrapper>
          {warning ? (
            <WarningText>
              <Markdown text={warning} />
            </WarningText>
          ) : null}

          <FormRow>
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={loadingTable}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
            />
          </FormRow>
        </>
      ) : null}
    </Container>
  );
};

export default ContactInfoSearch;
